import type { ReactNode } from "react";

import {
  CirclesThreeIcon,
  StackletLogo,
  Alert,
  Checkbox,
  Divider,
  Radio,
  RadioGroup,
} from "@stacklet/ui";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { useRefetchableFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { RepositoryPicker } from "app/components/RepositoryPicker";
import { ROLES } from "app/constants/rbac";
import { AccountGroupPicker } from "app/views/admin/bindings/components/AccountGroupPicker";
import { PolicyCollectionPicker } from "app/views/admin/bindings/components/PolicyCollectionPicker";

import type { RoleFormValues } from "../UpdateGroupForm/GroupSchema";
import type { ScopePicker_scopes$key } from "./__generated__/ScopePicker_scopes.graphql";
import type { ScopePickerRefetchQuery } from "./__generated__/ScopePickerRefetchQuery.graphql";

interface Props {
  /** The selected scope value */
  value?: { accountGroups?: string[] } | string;
  /** Callback when scope selection changes */
  onChange: (scope: { accountGroups: string[] } | string) => void;
  /** Fragment reference for scopes data */
  queryRef: ScopePicker_scopes$key;
  /** Current selected role */
  selectedRole?: string;
  /** Index of the current role in the form array */
  index: number;
}

interface RadioLabelWithIconProps {
  /** Main title text */
  title: string;
  /** Optional code/monospace text */
  code?: string;
  /** Description text */
  description: string;
  /** Icon component */
  icon?: ReactNode;
}

function RadioLabelWithIcon({
  title,
  code,
  description,
  icon,
}: RadioLabelWithIconProps) {
  return (
    <div className="ml-2 max-w-[21.875rem]">
      <div className="flex flex-row items-center gap-xxs">
        {icon}
        <span className="font-extrabold">{title}</span>
        {code ? <span className="font-mono">{code}</span> : null}
      </div>
      <p className="text-label-small">{description}</p>
    </div>
  );
}

export function ScopePicker({
  queryRef: scopesRef,
  selectedRole,
  index,
}: Props) {
  const { control } = useFormContext<{
    roles: RoleFormValues[];
  }>();

  // Watch all roles to check if there are other non-admin roles selected
  const allRoles = useWatch({
    control,
    name: "roles",
  }) as RoleFormValues[] | undefined;

  const [data] = useRefetchableFragment<
    ScopePickerRefetchQuery,
    ScopePicker_scopes$key
  >(
    graphql`
      fragment ScopePicker_scopes on Query
      @argumentDefinitions(first: { type: "Int", defaultValue: 10 })
      @refetchable(queryName: "ScopePickerRefetchQuery") {
        ...AccountGroupPicker_accountGroups @arguments(first: $first)
        ...PolicyCollectionPicker_policyCollections @arguments(first: $first)
        ...RepositoryPicker_repositoryConfigs @arguments(first: $first)
      }
    `,
    scopesRef,
  );

  if (selectedRole === "stacklet-user") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_SYSTEM_ALL">
                <RadioLabelWithIcon
                  code="(system:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<StackletLogo height={13} width={13} />}
                  title="System"
                />
              </Radio>
              <Radio className="items-start" value="REVOKE_SYSTEM_ALL">
                <div className="ml-2">
                  <p>No Login Permissions</p>
                  <p className="text-label-small">
                    Users cannot login to any Stacklet App
                  </p>
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "viewer") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_SYSTEM_ALL">
                <RadioLabelWithIcon
                  description={ROLES[selectedRole]?.description.short}
                  icon={<StackletLogo height={13} width={13} />}
                  title="System"
                />
              </Radio>
              <Radio className="items-start" value="REVOKE_SYSTEM_ALL">
                <div className="ml-2">
                  <p>No View Permissions</p>
                  <p className="text-label-small">
                    Users cannot view any Stacklet objects
                  </p>
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "resource-viewer") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_ACCOUNTS_ALL">
                <RadioLabelWithIcon
                  code="(account-group:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<CirclesThreeIcon height={13} width={13} />}
                  title="All Account Groups"
                />
              </Radio>
              <Radio className="items-start" value="TARGETS_SELECT">
                <div className="ml-2">
                  <p>Select Account Groups</p>
                  <p className="text-label-small">
                    Select specific Account Groups to view resources and
                    resource matches
                  </p>
                  <div
                    className="mt-xs max-w-[400px]"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <AccountGroupPicker
                      disabled={field.value !== "TARGETS_SELECT"}
                      fieldName={`roles.${index}.resourceViewerOptions.accountGroups`}
                      label="Account groups"
                      placeholder="Account groups"
                      queryRef={data}
                      multiple
                    />
                  </div>
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "creator") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_SYSTEM_ALL">
                <RadioLabelWithIcon
                  code="(system:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<StackletLogo height={13} width={13} />}
                  title="System"
                />
              </Radio>
              <Radio className="items-start" value="TARGETS_SELECT">
                <div className="ml-2">
                  <p>Creator role for:</p>
                  <p className="text-label-small">
                    Select which objects this role can create
                  </p>
                  <div className="mt-xs flex flex-col">
                    <Controller
                      control={control}
                      defaultValue={false}
                      disabled={field.value !== "TARGETS_SELECT"}
                      name={`roles.${index}.creatorOptions.accountGroups`}
                      render={({
                        field: {
                          value: checkboxValue,
                          onChange: checkboxOnChange,
                        },
                      }) => (
                        <Checkbox
                          isDisabled={field.value !== "TARGETS_SELECT"}
                          isSelected={checkboxValue as boolean}
                          onChange={checkboxOnChange}
                        >
                          <span className="text-label-small">
                            Account Groups
                          </span>
                        </Checkbox>
                      )}
                    />
                    <Controller
                      control={control}
                      defaultValue={false}
                      disabled={field.value !== "TARGETS_SELECT"}
                      name={`roles.${index}.creatorOptions.policyCollections`}
                      render={({
                        field: {
                          value: checkboxValue,
                          onChange: checkboxOnChange,
                        },
                      }) => (
                        <Checkbox
                          isDisabled={field.value !== "TARGETS_SELECT"}
                          isSelected={checkboxValue as boolean}
                          onChange={checkboxOnChange}
                        >
                          <span className="text-label-small">
                            Policy Collections
                          </span>
                        </Checkbox>
                      )}
                    />
                    <Controller
                      control={control}
                      defaultValue={false}
                      disabled={field.value !== "TARGETS_SELECT"}
                      name={`roles.${index}.creatorOptions.policyRepositories`}
                      render={({
                        field: {
                          value: checkboxValue,
                          onChange: checkboxOnChange,
                        },
                      }) => (
                        <Checkbox
                          isDisabled={field.value !== "TARGETS_SELECT"}
                          isSelected={checkboxValue as boolean}
                          onChange={checkboxOnChange}
                        >
                          <span className="text-label-small">
                            Policy Repositories
                          </span>
                        </Checkbox>
                      )}
                    />
                  </div>
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "editor") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_SYSTEM_ALL">
                <RadioLabelWithIcon
                  code="(system:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<StackletLogo height={13} width={13} />}
                  title="System"
                />
              </Radio>
              <Radio className="items-start" value="TARGETS_SELECT">
                <div className="ml-2 w-full">
                  <p>Editor role for:</p>
                  <p className="text-label-small">
                    Select which objects this role can edit
                  </p>
                  {field.value === "TARGETS_SELECT" ? (
                    <div
                      className="mt-xs max-w-[400px]"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <AccountGroupPicker
                        disabled={false}
                        fieldName={`roles.${index}.editorOptions.accountGroups`}
                        label="Account groups"
                        placeholder="Account groups"
                        queryRef={data}
                        multiple
                      />
                      <PolicyCollectionPicker
                        disabled={false}
                        fieldName={`roles.${index}.editorOptions.policyCollections`}
                        label="Policy collections"
                        placeholder="Policy collections"
                        queryRef={data}
                        multiple
                      />
                      <RepositoryPicker
                        disabled={false}
                        fieldName={`roles.${index}.editorOptions.policyRepositories`}
                        label="Repositories"
                        placeholder="Repositories"
                        queryRef={data}
                      />
                    </div>
                  ) : null}
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "admin") {
    // Check if there are other roles besides admin
    const hasOtherRoles =
      allRoles?.some(
        (roleItem, roleIndex) =>
          roleIndex !== index && roleItem.role && roleItem.role !== "admin",
      ) || false;

    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_SYSTEM_ALL">
                <RadioLabelWithIcon
                  code="(system:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<StackletLogo height={13} width={13} />}
                  title="System"
                />
              </Radio>
              <Radio className="items-start" value="REVOKE_SYSTEM_ALL">
                <div className="ml-2">
                  <p>No Admin Permissions</p>
                  <p className="text-label-small">
                    Users cannot manage any Stacklet objects
                  </p>
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        {hasOtherRoles ? (
          <Alert className="my-4" severity="info">
            The admin role supersedes all other roles selected.
          </Alert>
        ) : null}
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "policy-deployer") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_ACCOUNTS_ALL">
                <RadioLabelWithIcon
                  code="(account-group:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<CirclesThreeIcon height={13} width={13} />}
                  title="All Account Groups"
                />
              </Radio>
              <Radio className="items-start" value="TARGETS_SELECT">
                <div className="ml-2">
                  <p>Select Account Groups</p>
                  <p className="text-label-small">
                    Select specific Account Groups to deploy policies to
                  </p>
                  <div
                    className="mt-xs flex w-full max-w-[400px] flex-col gap-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <AccountGroupPicker
                      disabled={field.value !== "TARGETS_SELECT"}
                      fieldName={`roles.${index}.policyDeployerOptions.accountGroups`}
                      label="Account groups"
                      placeholder="Account groups"
                      queryRef={data}
                      multiple
                    />
                  </div>
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  if (selectedRole === "owner") {
    return (
      <div>
        <Controller
          control={control}
          name={`roles.${index}.scope`}
          render={({ field }) => (
            <RadioGroup {...field} label="Target" orientation="horizontal">
              <Radio className="items-start" value="GRANT_SYSTEM_ALL">
                <RadioLabelWithIcon
                  code="(system:all)"
                  description={ROLES[selectedRole]?.description.short}
                  icon={<StackletLogo height={13} width={13} />}
                  title="System"
                />
              </Radio>
              <Radio className="items-start" value="TARGETS_SELECT">
                <div className="ml-2">
                  <p>Owner role for:</p>
                  <p className="text-label-small">
                    Select which objects this role can manage
                  </p>
                  {field.value === "TARGETS_SELECT" ? (
                    <div
                      className="mt-xs flex w-full max-w-[400px] flex-col gap-2"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <AccountGroupPicker
                        disabled={false}
                        fieldName={`roles.${index}.ownerOptions.accountGroups`}
                        label="Account groups"
                        placeholder="Account groups"
                        queryRef={data}
                        multiple
                      />
                      <PolicyCollectionPicker
                        disabled={false}
                        fieldName={`roles.${index}.ownerOptions.policyCollections`}
                        label="Policy collections"
                        placeholder="Policy collections"
                        queryRef={data}
                        multiple
                      />
                      <RepositoryPicker
                        disabled={false}
                        fieldName={`roles.${index}.ownerOptions.policyRepositories`}
                        label="Repositories"
                        placeholder="Repositories"
                        queryRef={data}
                      />
                    </div>
                  ) : null}
                </div>
              </Radio>
            </RadioGroup>
          )}
        />
        <Divider className="mt-lg" />
      </div>
    );
  }

  return null;
}
